/**
 * fb-layout.ts — Pure tree auto-layout engine. Ported from flow-builder-layout.jsx.
 */
import { FB_NODE_W, FB_GAP_X, FB_SLOT, FB_DGAP, FB_ENDH, FB_TRIG_H, fbIsTerminal, fbNodeHeight, fbBranchLabels } from "./fb-model";
import type { FBFlow, FBStep } from "./fb-model";

export interface NodeRect { step?: FBStep; trigger?: boolean; id?: string; x:number; y:number; w:number; h:number; }
export interface Link { from:{x:number;y:number}; to:{x:number;y:number}; color:string; d:string; }
export interface Slot { id:string; x:number; y:number; ctx:{ parentId:string|null; branch:"yes"|"no"|null }; index:number; pulse?:boolean; }
export interface BranchLabel { x:number; y:number; text:string; color:string; tint:string; }
export interface End { x:number; y:number; text:string; }
export interface FBLayout { nodes:NodeRect[]; links:Link[]; slots:Slot[]; labels:BranchLabel[]; ends:End[]; width:number; height:number; }

type Ctx = { parentId:string|null; branch:"yes"|"no"|null };

function fbLinkPath(l: Omit<Link,"d">): string {
  const f=l.from, t=l.to;
  if (Math.abs(f.x-t.x)<0.6) return `M ${f.x} ${f.y} L ${t.x} ${t.y}`;
  const dy = Math.max(18,(t.y-f.y)*0.5);
  return `M ${f.x} ${f.y} C ${f.x} ${f.y+dy}, ${t.x} ${t.y-dy}, ${t.x} ${t.y}`;
}

interface Meas { empty?:boolean; els:any[]; steps:FBStep[]; ctx:Ctx; w:number; h:number; spineX:number; }
interface MeasDec extends Meas { kind:"decision"; step:FBStep; yes:Meas; no:Meas; childrenW:number; nodeH:number; }

type NH = (s: FBStep) => number;

function measList(steps: FBStep[], ctx: Ctx, nh: NH): Meas {
  const els: any[] = steps.map((s) => s.kind==="decision" ? measDecision(s, nh) : { kind:"node", step:s, w:FB_NODE_W, h:nh(s), spineX:FB_NODE_W/2 });
  if (els.length===0) return { empty:true, ctx, steps, w:FB_NODE_W, h:FB_SLOT+FB_ENDH, spineX:FB_NODE_W/2, els:[] };
  const spineX = Math.max(...els.map((e) => e.spineX));
  let w=0; els.forEach((e) => { w=Math.max(w,(spineX-e.spineX)+e.w); });
  let h=FB_SLOT; els.forEach((e,i) => { h+=e.h; if (!fbIsTerminal(steps[i])) h+=FB_SLOT; });
  if (!fbIsTerminal(steps[steps.length-1])) h+=FB_ENDH;
  return { els, steps, ctx, w, h, spineX };
}

function measDecision(s: FBStep, nh: NH): MeasDec {
  if (s.kind!=="decision") throw new Error("not decision");
  const yes = measList(s.yes, { parentId:s.id, branch:"yes" }, nh);
  const no  = measList(s.no,  { parentId:s.id, branch:"no" }, nh);
  const childrenW = yes.w+FB_GAP_X+no.w;
  const nodeH = nh(s);
  const w = Math.max(FB_NODE_W, childrenW);
  return { kind:"decision", step:s, yes, no, childrenW, nodeH, w, spineX:w/2, h:nodeH+FB_DGAP+Math.max(yes.h,no.h), els:[], steps:[s], ctx:{ parentId:null, branch:null } };
}

/** opts (ADDITIVE — existing callers unaffected):
 *  - nodeHeight: override the per-step height (e.g. measured DOM heights so
 *    variable-length page nodes never overlap).
 *  - noTrigger: omit the trigger node — the flow starts at the first step. */
export function fbLayout(flow: FBFlow, opts?: { nodeHeight?: (s: FBStep) => number; noTrigger?: boolean }): FBLayout {
  const nh: NH = opts?.nodeHeight ?? fbNodeHeight;
  const nodes: NodeRect[] = [], links: Omit<Link,"d">[] = [], slots: Slot[] = [], labels: BranchLabel[] = [], ends: End[] = [];

  function drawList(blk: Meas, ax: number, ay: number, topFromY: number|null, noTop?: boolean) {
    const sx = ax+blk.spineX;
    if (blk.empty) {
      slots.push({ id:`sl|${blk.ctx.parentId||"root"}|${blk.ctx.branch||"-"}|0`, x:sx, y:ay+FB_SLOT/2, ctx:blk.ctx, index:0, pulse:!blk.ctx.parentId });
      if (topFromY!=null) links.push({ from:{x:sx,y:topFromY}, to:{x:sx,y:ay+FB_SLOT/2}, color:blk.ctx.branch==="yes"?"#1F8A5B":blk.ctx.branch==="no"?"#C0392B":"#CDD3DD" });
      ends.push({ x:sx, y:ay+FB_SLOT+FB_ENDH/2, text:blk.ctx.parentId?"":"Flow ends" });
      return;
    }
    let y=ay;
    let prevBottom: number;
    if (noTop) {
      // No lead-in slot/connector — the flow starts AT the first node.
      prevBottom = y;
    } else {
      slots.push({ id:`sl|${blk.ctx.parentId||"root"}|${blk.ctx.branch||"-"}|0`, x:sx, y:y+FB_SLOT/2, ctx:blk.ctx, index:0 });
      if (topFromY!=null) links.push({ from:{x:sx,y:topFromY}, to:{x:sx,y:y+FB_SLOT/2}, color:blk.ctx.branch==="yes"?"#1F8A5B":blk.ctx.branch==="no"?"#C0392B":"#CDD3DD" });
      prevBottom=y+FB_SLOT/2;
      y+=FB_SLOT;
    }
    blk.els.forEach((e: any, i: number) => {
      const ex=ax+blk.spineX-e.spineX;
      links.push({ from:{x:sx,y:prevBottom}, to:{x:sx,y}, color:"#CDD3DD" });
      if (e.kind==="decision") drawDecision(e,ex,y); else nodes.push({ step:e.step, x:ex, y, w:FB_NODE_W, h:e.h });
      const bottom=y+e.h;
      if (!fbIsTerminal(blk.steps[i])) {
        slots.push({ id:`sl|${blk.ctx.parentId||"root"}|${blk.ctx.branch||"-"}|${i+1}`, x:sx, y:bottom+FB_SLOT/2, ctx:blk.ctx, index:i+1 });
        links.push({ from:{x:sx,y:bottom}, to:{x:sx,y:bottom+FB_SLOT/2}, color:"#CDD3DD" });
        prevBottom=bottom+FB_SLOT/2; y=bottom+FB_SLOT;
      } else { prevBottom=bottom; y=bottom; }
    });
    const isRoot=!(blk.ctx&&blk.ctx.parentId);
    if (!fbIsTerminal(blk.steps[blk.steps.length-1])) {
      links.push({ from:{x:sx,y:prevBottom}, to:{x:sx,y:y+FB_ENDH/2}, color:"#CDD3DD" });
      ends.push({ x:sx, y:y+FB_ENDH/2, text:isRoot?"Flow ends":"" });
    }
  }

  function drawDecision(e: MeasDec, ax: number, ay: number) {
    const s=e.step as { id:string; kind:"decision"; condition:string; yes:FBStep[]; no:FBStep[] };
    const nodeX=ax+e.spineX-FB_NODE_W/2;
    nodes.push({ step:s as FBStep, x:nodeX, y:ay, w:FB_NODE_W, h:e.nodeH });
    const groupLeft=ax+e.spineX-e.childrenW/2;
    const yesX=groupLeft, noX=groupLeft+e.yes.w+FB_GAP_X;
    const yesSpine=yesX+e.yes.spineX, noSpine=noX+e.no.spineX;
    labels.push({ x:yesSpine, y:ay+e.nodeH+22, text:"YES", color:"#1F8A5B", tint:"#E4F5EF" });
    labels.push({ x:noSpine,  y:ay+e.nodeH+22, text:"NO",  color:"#C0392B", tint:"#FCE9EC" });
    const branchY=ay+e.nodeH+FB_DGAP;
    links.push({ from:{x:ax+e.spineX,y:ay+e.nodeH}, to:{x:yesSpine,y:branchY}, color:"#1F8A5B" });
    links.push({ from:{x:ax+e.spineX,y:ay+e.nodeH}, to:{x:noSpine, y:branchY}, color:"#C0392B" });
    drawList(e.yes, yesX, branchY, null);
    drawList(e.no,  noX,  branchY, null);
  }

  const root=measList(flow.steps||[], { parentId:null, branch:null }, nh);
  const spineX=Math.max(FB_NODE_W/2,root.spineX);
  const trigX=spineX-FB_NODE_W/2, rootX=spineX-root.spineX;
  const width=Math.max(trigX+FB_NODE_W, rootX+root.w);
  let height: number;
  if (opts?.noTrigger) {
    // The flow starts AT the first step — no trigger node and no add-slot above it.
    drawList(root, rootX, 6, null, true);
    height=root.h+16;
  } else {
    nodes.push({ trigger:true, id:"trigger", x:trigX, y:0, w:FB_NODE_W, h:FB_TRIG_H });
    drawList(root, rootX, FB_TRIG_H, FB_TRIG_H-6);
    height=FB_TRIG_H+root.h+10;
  }
  const finalLinks: Link[] = links.map((l) => ({ ...l, d:fbLinkPath(l) }));
  return { nodes, links:finalLinks, slots, labels, ends, width, height };
}
